import React from 'react';
import DollarPriceInput, {
  type DollarPriceInputProps,
} from '@wix/da-shared-react/pkg/DollarPriceInput';
import PriceInputWithEarnings from '@wix/da-shared-react/pkg/DollarPriceInput/variants/PriceInputWithEarnings';
import { useSubmitField } from '../../hooks/useSubmitField';
import { useInitAdapter, submitAdapter, cleanSubmitAdapter } from './adapters';
import { useValidation } from './validation';

interface Props extends Pick<DollarPriceInputProps, 'className' | 'label' | 'subLabel'> {
  required?: boolean;
  withEarnings?: boolean;

  /**
   * By default, the PCP fields only send their data on submit after they've been altered by the user.
   *
   * Toggling this prop allows us to bypass this isDirty check and submit with the PCP data regardless. Useful in
   * smaller, customized submit flows.
   */
  allowCleanFieldSubmit?: boolean;
}
const PriceField: React.FC<Props> = ({
  required,
  withEarnings,
  allowCleanFieldSubmit,
  ...rest
}) => {
  const validator = useValidation();
  const initAdapter = useInitAdapter();

  const {
    error,
    field: { ref, ...fieldProps },
  } = useSubmitField('downloadDollarPrice', {
    initAdapter,
    submitAdapter: allowCleanFieldSubmit ? cleanSubmitAdapter : submitAdapter,
    submitIfCleanOnEdit: true,
    validator,
  });

  if (withEarnings) {
    return (
      <PriceInputWithEarnings
        {...fieldProps}
        error={error?.message}
        isRequired={required}
        ref={ref}
        forceInteger
        productType="pcp"
        {...rest}
      />
    );
  }

  return (
    <DollarPriceInput
      {...fieldProps}
      error={error?.message}
      isRequired={required}
      ref={ref}
      forceInteger
      {...rest}
    />
  );
};
PriceField.displayName = 'PriceField';
export default PriceField;
